import { lazy, Suspense } from "react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import MainLayout from "@/components/layouts/MainLayout";
import { SearchProvider } from "./contexts/SearchContext";
import SearchModal from "./components/search/SearchModal";
import ScrollToTop from './components/effect/ScrollToTop';
import PageMetaTracker from './components/effect/PageMetaTracker';

// Lazy load page components for optimal bundle splitting
const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const Research = lazy(() => import("@/pages/Research"));
const ResearchProjectDetail = lazy(() => import("./pages/ResearchProjectDetail"));
const CoursesList = lazy(() => import("@/pages/CoursesList"));
const Courses = lazy(() => import("@/pages/Courses"));
const Internships = lazy(() => import("@/pages/Internships"));
const InternshipDetail = lazy(() => import("@/pages/InternshipDetail"));
const PhD = lazy(() => import("@/pages/PhD"));
const Publications = lazy(() => import("@/pages/Publications"));
const BooksJournal = lazy(() => import("./pages/Books-Journal"));
const Ebooks = lazy(() => import("@/pages/Ebook"));
const Exhibitions = lazy(() => import("@/pages/Exhibitions"));
const Newsletters = lazy(() => import("./pages/Newsletter"));
const Reports = lazy(() => import("./pages/Reports"));
const Blogs = lazy(() => import("@/pages/Blogs"));
const BlogDetail = lazy(() => import("@/pages/BlogDetail"));
const Author = lazy(() => import("@/pages/Author"));
const Events = lazy(() => import("@/pages/Events"));
const MediaEventDetail = lazy(() => import("@/pages/MediaEventDetail"));
const WorkshopDetail = lazy(() => import("@/pages/WorkshopDetail"));
const ConferenceDetail = lazy(() => import("@/pages/ConferenceDetail"));
const LectureSeries = lazy(() => import("@/pages/LectureSeries"));
const GuestLecture = lazy(() => import("@/pages/GuestLecture"));
const BookTalk = lazy(() => import("@/pages/BookTalk"));
const Lecture = lazy(() => import("@/pages/lectures"));
const MediaGallery = lazy(() => import("@/pages/MediaGallery"));
const Offerings = lazy(() => import("@/pages/Offering"));
const OfferingDetail = lazy(() => import("@/pages/OfferingDetail"));
const FlipbookViewer = lazy(() => import("./pages/FlipbookViewer"));
const EventFlipbookViewer = lazy(() => import("./pages/EventFlipbookViewer"));
const AnnualReportFlipbookViewer = lazy(() => import("./pages/AnnualReportFlipbookViewer"));
const ExhibitionFlipbookViewer = lazy(() => import("./pages/ExhibitionFlipbookViewer"));
const Contact = lazy(() => import("@/pages/Contact"));
const NotFound = lazy(() => import("@/pages/not-found"));

function Router() {
  return (
    <>
      <ScrollToTop />
      <PageMetaTracker />
      <Suspense fallback={
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#CC0033]"></div>
        </div>
      }>
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/about" component={About} />
          {/* About page tab routes */}
          <Route path="/about/rishihood" component={About} />
          <Route path="/about/chs" component={About} />
          <Route path="/about/vision-mission" component={About} />
          <Route path="/about/team" component={About} />
          <Route path="/about/advisory-board" component={About} />
          <Route path="/about/research-advisory" component={About} />
          <Route path="/about/internship-advisory" component={About} />
          <Route path="/about/directors-note" component={About} />

          <Route path="/research-projects" component={Research} />
          <Route path="/research-projects/:slug" component={ResearchProjectDetail} />
          <Route path="/courses" component={CoursesList} />
          <Route path="/courses/:slug" component={Courses} />
          <Route path="/internships" component={Internships} />
          <Route path="/internships/:slug" component={InternshipDetail} />
          <Route path="/phd" component={PhD} />

          {/* Publications page routes */}
          <Route path="/publications" component={Publications} />
          <Route path="/publications/books-and-journals" component={BooksJournal} />
          <Route path="/publications/ebooks" component={Ebooks} />
          <Route path="/publications/exhibitions" component={Exhibitions} />
          <Route path="/publications/newsletter" component={Newsletters} />

          <Route path="/publications/reports" component={Reports} />
          <Route path="/publications/reports/project-reports" component={Reports} />
          <Route path="/publications/reports/events-and-conferences" component={Reports} />
          <Route path="/publications/reports/annual-report" component={Reports} />

          <Route path="/blogs" component={Blogs} />
          <Route path="/blogs/:id/:slug" component={BlogDetail} />
          <Route path="/authors/:id/:slug" component={Author} />
          <Route path="/authors/:id" component={Author} />

          {/* Events page routes */}
          <Route path="/events" component={Events} />
          <Route path="/events/conferences" component={Events} />
          <Route path="/events/centre" component={Events} />
          <Route path="/events/workshops" component={Events} />
          <Route path="/events/media" component={Events} />
          <Route path="/events/media/:slug" component={MediaEventDetail} />
          <Route path="/events/workshop/:slug" component={WorkshopDetail} />
          <Route path="/events/conference/:slug" component={ConferenceDetail} />

          {/* lecture page routes */}
          <Route path="/lectures/lecture-series/:slug" component={LectureSeries} />

          <Route path="/lectures/guest-lecture/:slug" component={GuestLecture} />
          <Route path="/lectures/book-talk/:slug" component={BookTalk} />

          <Route path="/lectures/lectureseries" component={Lecture} />
          <Route path="/lectures/guestlectures" component={Lecture} />
          <Route path="/lectures/booktalk" component={Lecture} />
          <Route path="/lectures" component={Lecture} />

          <Route path="/media-gallery" component={MediaGallery} />
          <Route path="/offerings" component={Offerings} />
          <Route path="/offerings/:slug" component={OfferingDetail} />
          <Route path="/publications/ebooks/:id" component={FlipbookViewer} />
          <Route path="/publications/reports/events/:id" component={EventFlipbookViewer} />
          <Route path="/publications/reports/annual/:id" component={AnnualReportFlipbookViewer} />
          <Route path="/publications/exhibitions/:id" component={ExhibitionFlipbookViewer} />
          <Route path="/contact" component={Contact} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <SearchProvider>
      <QueryClientProvider client={queryClient}>
        <MainLayout>
          <Router />
        </MainLayout>
        <SearchModal />
        <Toaster />
      </QueryClientProvider>
    </SearchProvider>
  );
}

export default App;
